import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Inbox, MessageSquare, Mail, Send, Search, RefreshCw, Check, CheckCheck, Circle, Archive, Clock, User, Phone, Settings, AlertCircle, ChevronDown, ArrowLeft, Facebook, Instagram, Voicemail, MessageCircle } from 'lucide-react';
import { neonQuery } from '../services/api';
import { getCurrentUser } from '../services/auth';

const INBOX_PROXY = window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions/inbox-proxy'
  : '/.netlify/functions/inbox-proxy';

async function inboxCall(action, params = {}) {
  const res = await fetch(INBOX_PROXY, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, params }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Inbox API error ${res.status}`);
  return data;
}

function formatPhone(num) {
  if (!num) return '';
  const digits = String(num).replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return num;
}

function convTimestamp(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  if (isToday) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  if (isYesterday) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

const CHANNEL_CONFIG = {
  sms: { label: 'SMS', icon: Phone, color: '#2563eb', bg: '#eff6ff' },
  voicemail: { label: 'Voicemail', icon: Voicemail, color: '#059669', bg: '#ecfdf5' },
  email: { label: 'Email', icon: Mail, color: '#7c3aed', bg: '#f5f3ff' },
  meta: { label: 'Meta DM', icon: Facebook, color: '#1877f2', bg: '#eff6ff' },
  comment: { label: 'Meta Comment', icon: MessageCircle, color: '#ea580c', bg: '#fff7ed' },
};

function ChannelBadge({ channel }) {
  const cfg = CHANNEL_CONFIG[channel] || { label: channel, color: '#6b7280', bg: '#f3f4f6' };
  const Icon = cfg.icon || MessageSquare;
  return (
    <span className="inbox-channel-badge" style={{ background: cfg.bg, color: cfg.color }}>
      <Icon size={10} /> {cfg.label}
    </span>
  );
}

function StatusDot({ status }) {
  const colors = { open: '#f59e0b', closed: '#9ca3af', snoozed: '#6366f1' };
  return <Circle size={8} fill={colors[status] || '#f59e0b'} color={colors[status] || '#f59e0b'} />;
}

// ── Channel Setup Panel ──────────────────────────────────────────────────────

function ChannelSetup({ channelStatus, onRefresh }) {
  return (
    <div style={{ padding: 24 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)', margin: '0 0 16px' }}>Channel Connections</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {Object.entries(CHANNEL_CONFIG).map(([key, cfg]) => {
          const status = channelStatus?.[key];
          const Icon = cfg.icon;
          return (
            <div key={key} className="card" style={{ margin: 0 }}>
              <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={18} color={cfg.color} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--navy)' }}>{cfg.label}</div>
                    {status?.connected ? (
                      <div style={{ fontSize: 11, color: '#1a8a4a' }}>
                        Connected{status.provider ? ` via ${status.provider}` : ''}
                        {status.number ? ` (${formatPhone(status.number)})` : ''}
                        {status.mailbox ? ` (${status.mailbox})` : ''}
                        {status.pageName ? ` (${status.pageName})` : ''}
                      </div>
                    ) : (
                      <div style={{ fontSize: 11, color: '#c0392b' }}>
                        Not connected{status?.error ? ` — ${status.error}` : ''}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{
                  width: 10, height: 10, borderRadius: '50%',
                  background: status?.connected ? '#1a8a4a' : '#e5e7eb',
                }} />
              </div>
              {!status?.connected && (
                <div style={{ padding: '0 16px 14px', fontSize: 11, color: 'var(--gray-500)' }}>
                  {key === 'sms' && 'RingCentral credentials (RC_CLIENT_ID, RC_CLIENT_SECRET, RC_JWT) are needed. These should already be set from the RingCentral page.'}
                  {key === 'voicemail' && 'Uses the same RingCentral connection as SMS. If SMS is connected, voicemail will work automatically.'}
                  {key === 'email' && (
                    <>
                      To connect Outlook email:<br />
                      1. Go to <strong>portal.azure.com</strong> → App registrations → CPx Admin<br />
                      2. Add <strong>Mail.Read</strong> and <strong>Mail.Send</strong> application permissions<br />
                      3. Grant admin consent<br />
                      4. Create a client secret and set <strong>GRAPH_CLIENT_SECRET</strong> in Netlify env vars<br />
                      5. Set <strong>INBOX_EMAIL_MAILBOX</strong> to the shared mailbox (e.g. info@crabplace.com)
                    </>
                  )}
                  {key === 'meta' && (
                    <>
                      To connect Meta Messenger DMs:<br />
                      1. Go to <strong>developers.facebook.com</strong> → CrabPlace app<br />
                      2. Add <strong>Messenger</strong> product and subscribe to <strong>messages</strong> webhook<br />
                      3. Generate a <strong>Page Access Token</strong> with <code>pages_messaging</code> permission<br />
                      4. Set <strong>META_PAGE_ACCESS_TOKEN</strong> and <strong>META_PAGE_ID</strong> in Netlify env vars
                    </>
                  )}
                  {key === 'comment' && 'Uses the same Meta Page token as DMs. Syncs comments from your Facebook page posts and ads.'}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <button onClick={onRefresh} className="btn btn-outline" style={{ marginTop: 16, fontSize: 11 }}>
        <RefreshCw size={12} /> Re-check Connections
      </button>
    </div>
  );
}

// ── Conversation List ────────────────────────────────────────────────────────

function ConversationList({ conversations, selectedId, onSelect, loading, channelFilter, onChannelFilter, statusFilter, onStatusFilter, searchQuery, onSearch, onSync }) {
  return (
    <div className="inbox-list">
      {/* Search + Sync */}
      <div className="inbox-list-header">
        <div className="inbox-search-bar">
          <Search size={14} color="var(--gray-400)" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={e => onSearch(e.target.value)}
            className="inbox-search-input"
          />
        </div>
        <button onClick={onSync} className="inbox-sync-btn" title="Sync messages">
          <RefreshCw size={14} className={loading ? 'spinning' : ''} />
        </button>
      </div>

      {/* Channel filter tabs */}
      <div className="inbox-filter-bar">
        {[{ key: null, label: 'All' }, ...Object.entries(CHANNEL_CONFIG).map(([k, v]) => ({ key: k, label: v.label }))].map(f => (
          <button
            key={f.key || 'all'}
            className={`inbox-filter-tab ${channelFilter === f.key ? 'active' : ''}`}
            onClick={() => onChannelFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto' }}>
          <select
            value={statusFilter || 'open'}
            onChange={e => onStatusFilter(e.target.value || null)}
            className="inbox-status-select"
          >
            <option value="open">Open</option>
            <option value="">All</option>
            <option value="closed">Closed</option>
            <option value="snoozed">Snoozed</option>
          </select>
        </div>
      </div>

      {/* Conversation items */}
      <div className="inbox-list-items">
        {loading && !conversations.length ? (
          <div className="inbox-empty">Loading conversations...</div>
        ) : !conversations.length ? (
          <div className="inbox-empty">
            <Inbox size={32} color="var(--gray-300)" />
            <div style={{ marginTop: 8 }}>No conversations</div>
            <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 4 }}>
              Click sync to pull in messages
            </div>
          </div>
        ) : conversations.map(conv => (
          <div
            key={conv.id}
            className={`inbox-conv-item ${selectedId === conv.id ? 'selected' : ''} ${conv.unread_count > 0 ? 'unread' : ''}`}
            onClick={() => onSelect(conv)}
          >
            <div className="inbox-conv-avatar">
              {conv.channel === 'sms' && <Phone size={14} />}
              {conv.channel === 'voicemail' && <Voicemail size={14} />}
              {conv.channel === 'email' && <Mail size={14} />}
              {conv.channel === 'meta' && <Facebook size={14} />}
              {conv.channel === 'comment' && <MessageCircle size={14} />}
            </div>
            <div className="inbox-conv-content">
              <div className="inbox-conv-top">
                <span className="inbox-conv-name">
                  {conv.contact_name || conv.contact_identifier || 'Unknown'}
                </span>
                <span className="inbox-conv-time">{convTimestamp(conv.last_message_at)}</span>
              </div>
              <div className="inbox-conv-preview">
                {conv.last_message_preview || 'No messages yet'}
              </div>
              <div className="inbox-conv-meta">
                <ChannelBadge channel={conv.channel} />
                {conv.assigned_to && (
                  <span style={{ fontSize: 10, color: 'var(--gray-400)', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                    <User size={9} /> {conv.assigned_to}
                  </span>
                )}
              </div>
            </div>
            {conv.unread_count > 0 && (
              <span className="inbox-unread-badge">{conv.unread_count}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Thread View ──────────────────────────────────────────────────────────────

function ThreadView({ conversation, messages, loading, onSend, onBack, onStatusChange, onAssign, navigateTo }) {
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const user = getCurrentUser();
  const [showActions, setShowActions] = useState(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || sending) return;
    setSending(true);
    try {
      await onSend(replyText.trim());
      setReplyText('');
    } catch (err) {
      alert('Send failed: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  if (!conversation) {
    return (
      <div className="inbox-thread-empty">
        <Inbox size={48} color="var(--gray-200)" />
        <div style={{ fontSize: 14, color: 'var(--gray-400)', marginTop: 12 }}>Select a conversation</div>
      </div>
    );
  }

  return (
    <div className="inbox-thread">
      {/* Thread header */}
      <div className="inbox-thread-header">
        <button className="inbox-back-btn" onClick={onBack}>
          <ArrowLeft size={16} />
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)' }}>
              {conversation.contact_name || conversation.contact_identifier || 'Unknown'}
            </span>
            <ChannelBadge channel={conversation.channel} />
            <StatusDot status={conversation.status} />
          </div>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
            {conversation.channel === 'sms' && formatPhone(conversation.contact_identifier)}
            {conversation.channel === 'voicemail' && formatPhone(conversation.contact_identifier)}
            {conversation.channel === 'email' && conversation.contact_identifier}
            {conversation.channel === 'meta' && (conversation.contact_identifier || '')}
            {conversation.channel === 'comment' && (() => {
              try {
                const ctx = JSON.parse(conversation.contact_identifier);
                return (
                  <span>
                    {ctx.commentText ? (
                      <span title={ctx.commentText}>
                        Post: "{ctx.commentText.substring(0, 60)}{ctx.commentText.length > 60 ? '...' : ''}"
                      </span>
                    ) : null}
                    {ctx.postUrl && (
                      <a href={ctx.postUrl} target="_blank" rel="noopener noreferrer"
                        style={{ marginLeft: ctx.commentText ? 6 : 0, color: 'var(--orange)', fontWeight: 600, textDecoration: 'none' }}>
                        View Post
                      </a>
                    )}
                    {!ctx.commentText && !ctx.postUrl && 'Facebook post comment'}
                  </span>
                );
              } catch { return 'Facebook post comment'; }
            })()}
            {conversation.customer_id && (
              <span
                style={{ marginLeft: 8, color: 'var(--orange)', cursor: 'pointer', fontWeight: 600 }}
                onClick={() => navigateTo('customer-search', null, conversation.customer_id)}
              >
                View Customer
              </span>
            )}
          </div>
        </div>
        <div style={{ position: 'relative' }}>
          <button
            className="btn btn-ghost"
            style={{ fontSize: 11, padding: '4px 10px' }}
            onClick={() => setShowActions(!showActions)}
          >
            Actions <ChevronDown size={12} />
          </button>
          {showActions && (
            <div className="inbox-actions-dropdown">
              {conversation.status === 'open' ? (
                <>
                  <button onClick={() => { onStatusChange('closed'); setShowActions(false); }}>
                    <Archive size={12} /> Close
                  </button>
                  <button onClick={() => { onStatusChange('snoozed'); setShowActions(false); }}>
                    <Clock size={12} /> Snooze
                  </button>
                </>
              ) : (
                <button onClick={() => { onStatusChange('open'); setShowActions(false); }}>
                  <Inbox size={12} /> Reopen
                </button>
              )}
              <button onClick={() => {
                const name = prompt('Assign to (staff name):');
                if (name !== null) { onAssign(name || null); setShowActions(false); }
              }}>
                <User size={12} /> {conversation.assigned_to ? 'Reassign' : 'Assign'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="inbox-messages-scroll">
        {loading ? (
          <div className="inbox-empty">Loading messages...</div>
        ) : !messages.length ? (
          <div className="inbox-empty">No messages in this conversation</div>
        ) : (
          <>
            {messages.map((msg, i) => {
              const isOutbound = msg.direction === 'outbound';
              const showDate = i === 0 || new Date(msg.sent_at).toDateString() !== new Date(messages[i - 1].sent_at).toDateString();
              return (
                <React.Fragment key={msg.id || i}>
                  {showDate && (
                    <div className="inbox-date-divider">
                      <span>{new Date(msg.sent_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                    </div>
                  )}
                  <div className={`inbox-message ${isOutbound ? 'outbound' : 'inbound'}`}>
                    <div className="inbox-message-bubble">
                      <div className="inbox-message-body">{msg.body}</div>
                      <div className="inbox-message-meta">
                        <span>{formatDateTime(msg.sent_at)}</span>
                        {isOutbound && msg.sent_by && <span> · {msg.sent_by}</span>}
                        {isOutbound && (
                          <span style={{ marginLeft: 4 }}>
                            {msg.read_at ? <CheckCheck size={11} color="#1a8a4a" /> : <Check size={11} />}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Reply box */}
      <form className="inbox-reply-bar" onSubmit={handleSend}>
        <input
          type="text"
          value={replyText}
          onChange={e => setReplyText(e.target.value)}
          placeholder={`Reply via ${CHANNEL_CONFIG[conversation.channel]?.label || conversation.channel}...`}
          className="inbox-reply-input"
          disabled={sending}
        />
        <button type="submit" disabled={!replyText.trim() || sending} className="inbox-send-btn">
          <Send size={16} />
        </button>
      </form>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

export default function UnifiedInbox({ navigateTo }) {
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [channelFilter, setChannelFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState('open');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('inbox'); // inbox | settings
  const [channelStatus, setChannelStatus] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const user = getCurrentUser();

  // Background sync — pulls from external APIs into Neon, doesn't touch UI state
  const backgroundSync = useCallback(async () => {
    const results = {};
    try {
      try { results.sms = await inboxCall('sync-sms'); } catch (e) { results.sms = { error: e.message }; }
      try { results.voicemail = await inboxCall('sync-voicemail'); } catch (e) { results.voicemail = { error: e.message }; }
      try { results.email = await inboxCall('sync-email'); } catch (e) { results.email = { error: e.message }; }
      try { results.meta = await inboxCall('sync-meta'); } catch (e) { results.meta = { error: e.message }; }
      try { results.comments = await inboxCall('sync-comments'); } catch (e) { results.comments = { error: e.message }; }
    } catch (e) { /* ignore */ }
    setSyncResult(results);
  }, []);

  // Init tables + auto-sync on mount
  useEffect(() => {
    neonQuery('inbox_init').catch(() => {});
    loadChannelStatus();
    backgroundSync();
    const syncInterval = setInterval(backgroundSync, 2 * 60000);
    return () => clearInterval(syncInterval);
  }, [backgroundSync]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load conversations
  const loadConversations = useCallback(async () => {
    setLoading(true);
    try {
      if (searchQuery.length >= 2) {
        const res = await neonQuery('inbox_search', { q: searchQuery });
        setConversations(res || []);
      } else {
        const params = {};
        if (channelFilter) params.channel = channelFilter;
        if (statusFilter) params.status = statusFilter;
        const res = await neonQuery('inbox_conversations', params);
        setConversations(res || []);
      }
    } catch (e) {
      console.error('Load conversations error:', e);
    } finally {
      setLoading(false);
    }
  }, [channelFilter, statusFilter, searchQuery]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(loadConversations, 30000);
    return () => clearInterval(interval);
  }, [loadConversations]);

  // Load messages for selected conversation
  const loadMessages = useCallback(async (convId) => {
    if (!convId) return;
    setMessagesLoading(true);
    try {
      const res = await neonQuery('inbox_messages', { conversation_id: convId });
      setMessages(res || []);
      // Update unread count in local state
      setConversations(prev => prev.map(c =>
        c.id === convId ? { ...c, unread_count: 0 } : c
      ));
    } catch (e) {
      console.error('Load messages error:', e);
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  const handleSelectConversation = useCallback((conv) => {
    setSelectedConv(conv);
    loadMessages(conv.id);
  }, [loadMessages]);

  // Manual sync (button click) — sync then reload
  const handleSync = async () => {
    setSyncing(true);
    try {
      await backgroundSync();
      await loadConversations();
      if (selectedConv) await loadMessages(selectedConv.id);
    } finally {
      setSyncing(false);
    }
  };

  // Send message
  const handleSend = async (text) => {
    if (!selectedConv) return;
    const channel = selectedConv.channel;

    if (channel === 'sms') {
      const phone = selectedConv.external_id;
      await inboxCall('send-sms', {
        conversation_id: selectedConv.id,
        to: phone.length === 10 ? `+1${phone}` : `+${phone}`,
        text,
        sent_by: user?.staffName || 'Staff',
      });
    } else if (channel === 'email') {
      await inboxCall('send-email', {
        conversation_id: selectedConv.id,
        to: selectedConv.external_id,
        subject: 'Re: CrabPlace',
        body: text,
        sent_by: user?.staffName || 'Staff',
      });
    } else if (channel === 'meta') {
      // An Instagram conversation is stored on the same 'meta' channel with an `ig:`
      // identifier, so both prefixes have to come off or the recipient id goes out
      // with the prefix still attached and Meta rejects the send.
      const recipientId = (selectedConv.contact_identifier || '').replace(/^(?:meta|ig):/, '');
      await inboxCall('send-meta', {
        conversation_id: selectedConv.id,
        recipient_id: recipientId,
        text,
        sent_by: user?.staffName || 'Staff',
      });
    } else if (channel === 'comment') {
      // For comments, we need the last inbound comment ID to reply to
      const lastInbound = [...messages].reverse().find(m => m.direction === 'inbound');
      const commentId = lastInbound?.channel_message_id?.replace('meta-comment-', '');
      if (commentId) {
        await inboxCall('reply-comment', {
          conversation_id: selectedConv.id,
          comment_id: commentId,
          text,
          sent_by: user?.staffName || 'Staff',
        });
      } else {
        throw new Error('No comment found to reply to');
      }
    } else {
      // Fallback: just store in DB
      await neonQuery('inbox_send', {
        conversation_id: selectedConv.id,
        body: text,
        sent_by: user?.staffName || 'Staff',
      });
    }

    await loadMessages(selectedConv.id);
    await loadConversations();
  };

  const handleStatusChange = async (status) => {
    if (!selectedConv) return;
    await neonQuery('inbox_update_status', { conversation_id: selectedConv.id, status });
    setSelectedConv(prev => ({ ...prev, status }));
    await loadConversations();
  };

  const handleAssign = async (assignedTo) => {
    if (!selectedConv) return;
    await neonQuery('inbox_assign', { conversation_id: selectedConv.id, assigned_to: assignedTo });
    setSelectedConv(prev => ({ ...prev, assigned_to: assignedTo }));
  };

  const loadChannelStatus = async () => {
    try {
      const status = await inboxCall('channel-status');
      setChannelStatus(status);
    } catch (e) {
      console.error('Channel status check failed:', e);
    }
  };

  // Count total unread
  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  return (
    <div className="inbox-container">
      {/* Page header */}
      <div className="inbox-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Inbox size={20} color="var(--navy)" />
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--navy)' }}>Unified Inbox</h2>
          {totalUnread > 0 && (
            <span className="inbox-total-unread">{totalUnread}</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {syncResult && (
            <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>
              {Object.entries(syncResult).filter(([, v]) => v.synced > 0).map(([k, v]) => `${k}: ${v.synced} new`).join(', ') || 'No new messages'}
            </span>
          )}
          {/* Channel status indicators */}
          <div style={{ display: 'flex', gap: 4 }}>
            {channelStatus && Object.entries(channelStatus).map(([ch, st]) => (
              <div key={ch} title={`${ch}: ${st.connected ? 'connected' : 'not connected'}`}
                style={{ width: 8, height: 8, borderRadius: '50%', background: st.connected ? '#1a8a4a' : '#e5e7eb' }} />
            ))}
          </div>
          <button
            className={`btn btn-ghost ${activeTab === 'settings' ? 'active' : ''}`}
            style={{ fontSize: 11, padding: '4px 8px' }}
            onClick={() => setActiveTab(activeTab === 'settings' ? 'inbox' : 'settings')}
          >
            <Settings size={14} />
          </button>
        </div>
      </div>

      {activeTab === 'settings' ? (
        <ChannelSetup channelStatus={channelStatus} onRefresh={loadChannelStatus} />
      ) : (
        <div className="inbox-layout">
          <ConversationList
            conversations={conversations}
            selectedId={selectedConv?.id}
            onSelect={handleSelectConversation}
            loading={loading || syncing}
            channelFilter={channelFilter}
            onChannelFilter={setChannelFilter}
            statusFilter={statusFilter}
            onStatusFilter={setStatusFilter}
            searchQuery={searchQuery}
            onSearch={setSearchQuery}
            onSync={handleSync}
          />
          <ThreadView
            conversation={selectedConv}
            messages={messages}
            loading={messagesLoading}
            onSend={handleSend}
            onBack={() => setSelectedConv(null)}
            onStatusChange={handleStatusChange}
            onAssign={handleAssign}
            navigateTo={navigateTo}
          />
        </div>
      )}
    </div>
  );
}
